import { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { useTranslation } from "@/i18n";
import { Progress } from "@/components/ui/progress";

const Differentiator = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"before" | "after">("after");

  const checklist = [
    t('differentiator.features.titles'),
    t('differentiator.features.metaDesc'),
    t('differentiator.features.longDesc'),
    t('differentiator.features.altText'),
  ];

  const isBefore = activeTab === "before";

  return (
    <section id="solution" className="py-20 md:py-28 relative overflow-hidden gradient-hero">
      {/* Background depth */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb glow-orb-primary w-[600px] h-[600px] -bottom-40 right-0 opacity-20" />
        <div className="glow-orb glow-orb-accent w-[500px] h-[500px] -top-32 -left-20 opacity-15" />
        <div className="absolute inset-0 grid-pattern opacity-40" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
              <Sparkles size={16} className="text-accent" />
              <span className="text-foreground">{t('differentiator.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              {t('differentiator.titlePre')}{' '}
              <span className="gradient-text">{t('differentiator.titleHighlight')}</span>
            </h2>
          </div>

          {/* Two column layout */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text + Checklist */}
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('differentiator.rankaroDesc')}
              </p>
              
              <div className="space-y-3">
                {checklist.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-xl glass-card border border-border/30 hover:border-primary/30 transition-all duration-300"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <span className="text-foreground font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-lg text-foreground">
                <span className="text-muted-foreground">{t('differentiator.keyMessage1')}</span>{" "}
                <span className="font-bold gradient-text">{t('differentiator.keyMessage2')}</span>
              </p>
            </div>

            {/* Right - Tabs Before/After */}
            <div>
              {/* Tab buttons */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setActiveTab("before")}
                  className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isBefore
                      ? "bg-destructive/15 text-destructive border border-destructive/30 shadow-[inset_0_2px_8px_-2px_hsl(var(--destructive)/0.2)]"
                      : "bg-muted/30 text-muted-foreground border border-border/30 hover:bg-muted/50"
                  }`}
                >
                  {t('differentiator.tabBefore')}
                </button>
                <button
                  onClick={() => setActiveTab("after")}
                  className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    !isBefore
                      ? "bg-primary/15 text-primary border border-primary/30 shadow-glow"
                      : "bg-muted/30 text-muted-foreground border border-border/30 hover:bg-muted/50"
                  }`}
                >
                  {t('differentiator.tabAfter')}
                </button>
              </div>

              {/* Tab content */}
              <div
                className={`rounded-2xl overflow-hidden transition-all duration-500 ${
                  isBefore
                    ? "glass-card border border-destructive/15 bg-destructive/5"
                    : "gradient-border-animated shadow-glow bg-primary/5"
                }`}
              >
                {/* Product header */}
                <div className={`p-4 border-b ${isBefore ? "bg-muted/20 border-destructive/10" : "bg-primary/5 border-primary/10"}`}>
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-16 h-16 rounded-lg flex items-center justify-center text-2xl transition-all duration-300 ${
                        isBefore
                          ? "bg-muted/50 opacity-60 grayscale"
                          : "bg-primary/10 border border-primary/20 shadow-[0_0_20px_-4px_hsl(var(--primary)/0.3)]"
                      }`}
                    >
                      👟
                    </div>
                    <div>
                      <p className={`text-xs ${isBefore ? "text-muted-foreground" : "text-primary"}`}>
                        {t('differentiator.exampleProduct')}
                      </p>
                      <p className="font-semibold text-foreground text-sm">{t('differentiator.exampleName')}</p>
                    </div>
                  </div>
                </div>

                {/* Content area */}
                <div className="p-5 space-y-4">
                  {isBefore ? (
                    <>
                      <Field label={t('differentiator.fieldTitle')} value={t('differentiator.beforeTitle')} variant="before" />
                      <Field label={t('differentiator.fieldMeta')} value={t('differentiator.beforeMeta')} variant="before" italic />
                      <Field label={t('differentiator.fieldDesc')} value={t('differentiator.beforeDesc')} variant="before" />
                      <Field label={t('differentiator.fieldLongDesc')} value={t('differentiator.beforeLongDesc')} variant="before" italic />
                      <Field label={t('differentiator.fieldAltText')} value={t('differentiator.beforeAltText')} variant="before" mono />
                      <div className="pt-3 border-t border-destructive/10">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-destructive" />
                          <span className="text-xs text-destructive font-medium">{t('differentiator.scoreBefore')}</span>
                        </div>
                        <Progress value={25} variant="critical" className="mt-2 h-2" />
                      </div>
                    </>
                  ) : (
                    <>
                      <Field label={t('differentiator.fieldTitle')} value={t('differentiator.afterTitle')} variant="after" bold />
                      <Field label={t('differentiator.fieldMeta')} value={t('differentiator.afterMeta')} variant="after" />
                      <Field label={t('differentiator.fieldDesc')} value={t('differentiator.afterDesc')} variant="after" />
                      <Field label={t('differentiator.fieldLongDesc')} value={t('differentiator.afterLongDesc')} variant="after" />
                      <Field label={t('differentiator.fieldAltText')} value={t('differentiator.afterAltText')} variant="after" bold />
                      <div className="pt-3 border-t border-primary/10">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-[hsl(142,71%,45%)] animate-pulse" />
                          <span className="text-xs text-[hsl(142,71%,45%)] font-medium">{t('differentiator.scoreAfter')}</span>
                        </div>
                        <Progress value={95} variant="success" className="mt-2 h-2" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({
  label,
  value,
  variant,
  italic,
  mono,
  bold,
}: {
  label: string;
  value: string;
  variant: "before" | "after";
  italic?: boolean;
  mono?: boolean;
  bold?: boolean;
}) => (
  <div>
    <p className={`text-xs mb-1 ${variant === "before" ? "text-destructive/60" : "text-primary/70"}`}>
      {label}
    </p>
    <p
      className={`text-sm ${
        variant === "before" ? "text-foreground/60" : "text-foreground"
      } ${italic ? "italic" : ""} ${mono ? "font-mono" : ""} ${bold ? "font-medium" : ""}`}
    >
      {value}
    </p>
  </div>
);

export default Differentiator;
